import { Component, computed, effect, inject, OnDestroy, OnInit, signal, Signal, Type } from '@angular/core';

import { CalendarWeekViewComponent } from '@lb/components/calendar-week-view/calendar-week-view.component';
import { ComponentLoaderService } from '@melodic-ui/services';
import { ICalendarWeekViewEvent } from '@lb/components/calendar-week-view/interfaces/icalendar-week-view-event.interface';
import { LessonPopupComponent } from '@lb/components/lessons/lesson-popup/lesson-popup.component';
import { ReadonlyLessonCardComponent } from '@lb/components/lessons/readonly-lesson-card/readonly-lesson-card.component';
import { PageHeaderComponent } from '@lb/components/page-header/page-header.component';
import { endOfDay, isSameDay, startOfDay } from 'date-fns';
import { GlobalTimezoneService } from '@lb/services/global-timezone.service';
import { TimezoneContextService } from '@lb/config/timezone.provider';
import { convertLessonDateForDisplay } from '@lb/functions/timezone-utils';
import { ICalendarWeekViewConfig } from '@lb/components/calendar-week-view/interfaces/icalendar-week-view-config.interface';
import { LearnerUserStateService } from '../../shared/state/learner-users-state.service';
import { CalendarQuickSyncButtonComponent } from '@lb/components/calendar-sync/calendar-quick-sync-button/calendar-quick-sync-button.component';
import { LessonV2Service } from '@lb/api/mylb/services';
import { ILessonListItem } from '@lb/api/mylb/interfaces';

@Component({
	selector: 'lrnr-calendar',
	standalone: true,
	imports: [CalendarWeekViewComponent],
	templateUrl: './calendar.component.html',
	styleUrl: './calendar.component.scss'
})
export class CalendarComponent implements OnInit, OnDestroy {
	private _learnerUserStateService = inject(LearnerUserStateService);
	private _componentLoaderService: ComponentLoaderService = inject(ComponentLoaderService);
	private _lessonV2Service = inject(LessonV2Service);
	private _globalTimezoneService: GlobalTimezoneService = inject(GlobalTimezoneService);
	private _timezoneContextService: TimezoneContextService = inject(TimezoneContextService);

	private _lessons = signal<ILessonListItem[]>([]);
	private _activeLearnerUserID = this._learnerUserStateService.activeLearnerUserID;
	private _currentRange = signal<{ startDate: Date; endDate: Date } | null>(null);

	public activeLearnerUserLoaded: Signal<boolean> = this._learnerUserStateService.activeUserLoaded;

	public lessonEvents: Signal<ICalendarWeekViewEvent[]> = computed(() => {
		const events: ICalendarWeekViewEvent[] = [];
		const selectedTz = this._globalTimezoneService.selectedTimezone();
		// Popup "Details" links open the lesson-context page (/lessons/:lessonId): the calendar sits outside
		// any one enrollment. The v1 card only renders that link for v2-eligible lessons, so no gate is needed.
		for (const lesson of this._lessons() || []) {
			const start = convertLessonDateForDisplay(lesson.lessonDate, selectedTz);
			const end = convertLessonDateForDisplay(lesson.lessonEndDate, selectedTz);
			if (isSameDay(start, end)) {
				events.push({
					eventID: lesson.lessonID,
					startDate: start,
					endDate: end,
					allDay: false,
					component: LessonPopupComponent as Type<Component>,
					inputs: () => ({
						lesson,
						lessonView: 'instructor',
						lessonCardComponent: ReadonlyLessonCardComponent,
						detailsLink: ['/lessons', lesson.lessonID]
					})
				});
			} else {
				// Lesson spans multiple days - split into separate events
				// First day: start → end of day
				events.push({
					eventID: lesson.lessonID,
					startDate: start,
					endDate: endOfDay(start),
					allDay: false,
					component: LessonPopupComponent as Type<Component>,
					inputs: () => ({
						lesson,
						lessonView: 'instructor',
						lessonCardComponent: ReadonlyLessonCardComponent,
						detailsLink: ['/lessons', lesson.lessonID]
					})
				});

				// Second day: start of day → end
				// Only create if end time is NOT at midnight (would create zero-duration event)
				const endDayStart = startOfDay(end);
				if (end.getTime() > endDayStart.getTime()) {
					events.push({
						eventID: `${lesson.lessonID}-end`,
						startDate: endDayStart,
						endDate: end,
						allDay: false,
						component: LessonPopupComponent as Type<Component>,
						inputs: () => ({
							lesson,
							lessonView: 'instructor',
							lessonCardComponent: ReadonlyLessonCardComponent,
							detailsLink: ['/lessons', lesson.lessonID]
						})
					});
				}
			}
		}
		return events;
	});

	public calendarConfiguration: ICalendarWeekViewConfig = {
		calendarHeaderActionsAreaComponent: CalendarQuickSyncButtonComponent as Type<Component>,
		calendarHeaderActionsAreaComponentInputs: {
			onClose: () => this.reloadCurrentRange()
		},
		timezoneInfo: computed(() => {
			const context = this._globalTimezoneService.timezoneContext();
			const selected = context.selectedTimezone;
			if (!selected) return { show: false, displayName: 'Unknown', abbreviation: 'UTC' };
			let abbr = selected.abbreviation || 'UTC';
			if (selected.timeZoneName) {
				try {
					const formatter = new Intl.DateTimeFormat('en-US', { timeZone: selected.timeZoneName, timeZoneName: 'short' });
					const parts = formatter.formatToParts(new Date());
					const tzPart = parts.find((p) => p.type === 'timeZoneName');
					if (tzPart?.value) abbr = tzPart.value;
				} catch {
					/* ignore */
				}
			}
			return { show: context.showTimezoneSelector, displayName: selected.displayName || selected.timeZoneName || 'Unknown', abbreviation: abbr };
		})
	};

	constructor() {
		// Sync context user timezone when selected timezone changes
		effect(() => {
			const selected = this._globalTimezoneService.selectedTimezone();
			if (selected) this._timezoneContextService.setUserTimezone(selected);
		});

		// Reload calendar when timezone changes
		effect(() => {
			const tz = this._globalTimezoneService.selectedTimezone();
			if (tz) this.reloadCurrentRange();
		});

		// Load lessons for active learner and date range
		effect(() => {
			const learnerUserID = this._activeLearnerUserID();
			const range = this._currentRange();
			if (learnerUserID && range) {
				this._lessonV2Service.getForDateRange(learnerUserID, 'learner', range.startDate, range.endDate).subscribe({
					next: (response) => {
						this._lessons.set(response.items);
					},
					error: (error) => {
						console.error('Error loading lessons for date range:', error);
					}
				});
			}
		});
	}

	ngOnInit(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
		this._componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: `My Calendar` });
	}

	ngOnDestroy(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(null);
	}

	calendarNav(weekStartStop: { startDate: Date; endDate: Date }): void {
		this._currentRange.set(weekStartStop);
	}
	private reloadCurrentRange(): void {
		const currentRange = this._currentRange();
		if (currentRange) this.calendarNav(currentRange);
	}
}
